import { cn } from '@/lib/utils.ts'
import { useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Input } from '@/components/ui/input.tsx'

import { DeleteLabelModal } from '@/components/DeleteLabelModal.tsx'
import { StoryLabel } from '@/components/form/StoryLabel.tsx'
import { useMutateLabel } from '@/requests/mutations/useMutateLabel.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'

type Props = {
  modes: AvailableMode[]
  label: Label
}

export const ManagedLabel = ({ label, modes }: Props) => {
  const [isEditMode, setIsEditMode] = useState(modes[0] === 'edit')
  const [name, setName] = useState(label.name)
  const [isDeleteModalShown, setIsDeleteModalShown] = useState(false)
  const { update } = useMutateLabel()

  const onSubmit = () => {
    if (!name) return

    update({
      ...label,
      name,
    }).then(() => setIsEditMode(false))
  }

  return (
    <div>
      {isEditMode ? (
        <div className="h-10 flex justify-between items-center bg-custom-onLight-hovered px-2 py-1">
          <img src="/images/label-icon.svg" alt="label icon" className="mr-[1px]" />
          <Input
            id={`label-content-input-${label.id}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation()
              onEnterInclusive(e, onSubmit)
            }}
            className="mr-1 p-1 bg-surface-backgroundElevated border-border-default-neutral hover:border-border-default-focused font-EN-KR-body2-regular text-contents-primary"
          />
          <Button type="button" size="icon" variant="icon" onClick={onSubmit} disabled={!name}>
            <img src="/images/label-rename-confirm-icon.svg" alt="confirm" />
          </Button>
        </div>
      ) : (
        <>
          <div
            className={cn(
              'flex justify-between items-center group py-1 px-2 h-10 bg-surface-componentBackground',
              'relative overflow-hidden after:pointer-events-none bg-transparent hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0',
            )}
          >
            <div className="flex items-center gap-1">
              <img src="/images/label-icon.svg" alt="label icon" />
              <div className="flex items-center gap-0.5">
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: */}
                <div
                  className="flex items-center gap-0.5 text-labelType-label cursor-pointer"
                  onClick={() => {
                    GlobalPubSub.publish(GlobalPubSub.Signals.SELECT_LABEL, name)
                    GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_LABEL_MANAGEMENT_MODAL, false)
                  }}
                >
                  <StoryLabel item={name} variant="text" />
                </div>
                <span className="font-EN-KR-body2-regular text-contents-primary">({label.count})</span>
              </div>
            </div>
            <div className="hidden gap-1 group-hover:flex">
              <Button
                type="button"
                size="icon"
                variant="icon"
                onClick={() => modes.includes('edit') && setIsEditMode(true)}
              >
                <img src="/images/label-rename-icon.svg" alt="rename" />
              </Button>
              <Button type="button" size="icon" variant="icon" onClick={() => setIsDeleteModalShown(true)}>
                <img src="/images/story-delete-icon.svg" alt="delete" />
              </Button>
            </div>
          </div>
          {isDeleteModalShown && <DeleteLabelModal label={label} onClose={() => setIsDeleteModalShown(false)} />}
        </>
      )}
    </div>
  )
}
